import { useEffect, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui-helpers";
import { toast } from "sonner";
import { DatabaseBackup, Download, Upload, CheckCircle2, AlertTriangle, Loader2, X } from "lucide-react";

const CHUNK = 1024 * 1024; // ~1 MB pieces stay well under any server/ingress request limit
const RETRIES = 3;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
// plain-English message for any failure (never a bare "error 500")
const friendly = (e) => {
  const st = e?.response?.status;
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string" && st && st < 500) return detail;
  if (st === 413) return "That piece was too large for the server — please refresh the page and try again";
  if (st === 401 || st === 403) return "Your session expired — sign in again, then choose the file again";
  return "Upload interrupted — please check your connection and choose the file again";
};
const fmtBytes = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);
const label = (n) => n.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Admin → Settings: full backup (all settings, presets, rules, staff, notifications, data and files)
// downloaded as one .zip the same CRM can import later (upload → preview → apply).
export function BackupRestoreCard() {
  const { logout } = useAuth();
  const [summary, setSummary] = useState(null);
  const [withFiles, setWithFiles] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [upload, setUpload] = useState(null); // {pct} while uploading
  const [preview, setPreview] = useState(null);
  const [overwrite, setOverwrite] = useState(false);
  const [job, setJob] = useState(null);
  const ref = useRef();
  const poll = useRef();

  useEffect(() => { api.get("/admin/backup/summary").then((r) => setSummary(r.data)).catch(() => {}); return () => clearInterval(poll.current); }, []);

  const totalRecords = (summary?.collections || []).reduce((a, c) => a + c.count, 0);

  const doExport = async () => {
    setExporting(0);
    try {
      const res = await api.get(`/admin/backup/export?include_files=${withFiles}`, { responseType: "blob", timeout: 0,
        onDownloadProgress: (e) => setExporting(e.loaded) });
      const name = (res.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/)?.[1] || "chahal-crm-backup.zip";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("Backup downloaded", { description: "Keep it somewhere safe — it contains all client data and staff logins." });
    } catch (e) { toast.error(apiErr(e)); } finally { setExporting(null); }
  };

  const pick = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) { toast.error("Choose a CRM backup .zip file"); return; }
    const id = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`).replace(/[^a-zA-Z0-9-]/g, "");
    const total = Math.max(1, Math.ceil(file.size / CHUNK));
    setPreview(null); setJob(null); setUpload({ pct: 0 });
    try {
      let last;
      for (let i = 0; i < total; i++) {
        for (let attempt = 0; ; attempt++) {
          const fd = new FormData();
          fd.append("upload_id", id); fd.append("index", String(i)); fd.append("total", String(total));
          fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), "chunk");
          try {
            last = (await api.post("/admin/backup/upload", fd, { timeout: 120000 })).data;
            break;
          } catch (e) {
            const st = e?.response?.status;
            const retryable = !st || st >= 500 || st === 408 || st === 429;
            // the final piece also runs the preview: a 4xx there is a real answer (e.g. "not a CRM backup"), not a blip
            if (!retryable || attempt >= RETRIES) throw e;
            setUpload({ pct: Math.round((i / total) * 100), retrying: attempt + 1 });
            await sleep(1000 * (attempt + 1));
          }
        }
        setUpload({ pct: Math.round(((i + 1) / total) * 100) });
      }
      setPreview(last); setOverwrite(false);
    } catch (e) {
      toast.error(friendly(e));
      api.post("/admin/backup/cancel", { upload_id: id }).catch(() => {}); // free the stored pieces right away
    } finally { setUpload(null); }
  };

  const cancel = async () => {
    if (preview) await api.post("/admin/backup/cancel", { upload_id: preview.upload_id }).catch(() => {});
    setPreview(null);
  };

  const selfReplaced = (preview?.warnings || []).some((w) => w.startsWith("Your own login"));

  const apply = async () => {
    if (!window.confirm("Import this backup now? Settings, templates and staff are replaced by the backup's version; data is added. Nothing that isn't in the file is deleted.")) return;
    try {
      await api.post("/admin/backup/apply", { upload_id: preview.upload_id, overwrite_records: overwrite }).catch((e) => { throw new Error(friendly(e)); });
      setJob({ status: "running" });
      clearInterval(poll.current);
      poll.current = setInterval(async () => {
        try {
          const j = (await api.get(`/admin/backup/jobs/${preview.upload_id}`)).data;
          setJob(j);
          if (j.status === "done" || j.status === "failed") {
            clearInterval(poll.current);
            if (j.status === "done") {
              api.get("/admin/backup/summary").then((r) => setSummary(r.data)).catch(() => {});
              if (selfReplaced) { toast.success("Backup imported"); toast.message("Your login came from the backup — please sign in again"); setTimeout(() => logout?.(), 2500); }
              else { toast.success("Backup imported — reloading so every page shows the restored settings"); setTimeout(() => window.location.reload(), 2500); }
            } else toast.error(j.error || "Import failed");
          }
        } catch (e) {
          if (e?.response?.status === 401) { clearInterval(poll.current); toast.message("Import finished — please sign in again with your backup password"); setTimeout(() => logout?.(), 1500); }
        }
      }, 1500);
    } catch (e) { toast.error(e?.message || friendly(e)); }
  };

  const sum = (k) => Object.values(job?.stats || {}).reduce((a, s) => a + (s[k] || 0), 0);

  return (
    <Card className="p-5 mb-5" data-testid="backup-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0"><DatabaseBackup className="h-5 w-5" /></div>
        <div>
          <h3 className="font-semibold text-slate-900">Backup &amp; restore</h3>
          <p className="text-sm text-slate-500">One file with everything: every setting, preset, rule, template, staff login and permission, notification, plus all leads, clients, cases, invoices, payments, notes, tasks and uploaded files. This CRM (or a future copy of it) can import it.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="backup-export">
          <div className="font-semibold text-sm text-slate-800 flex items-center gap-2"><Download className="h-4 w-4 text-primary" /> Export</div>
          {summary && <div className="text-xs text-slate-500" data-testid="backup-summary">{totalRecords.toLocaleString()} records in {summary.collections.length} collections · {summary.files} uploaded files ({fmtBytes(summary.files_bytes)})</div>}
          <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" data-testid="backup-include-files" checked={withFiles} onChange={(e) => setWithFiles(e.target.checked)} /> Include uploaded files (documents, payment proofs, signed PDFs)</label>
          <p className="text-[11px] text-slate-400">Stripe/PayPal secret keys are never included — re-enter them after importing.</p>
          <button data-testid="backup-export-btn" onClick={doExport} disabled={exporting !== null}
            className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-60">
            {exporting !== null ? <><Loader2 className="h-4 w-4 animate-spin" /> Preparing… {exporting ? fmtBytes(exporting) : ""}</> : <><Download className="h-4 w-4" /> Download full backup</>}
          </button>
        </div>

        <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="backup-import">
          <div className="font-semibold text-sm text-slate-800 flex items-center gap-2"><Upload className="h-4 w-4 text-primary" /> Import</div>
          <p className="text-xs text-slate-500">Upload a backup file — you&apos;ll see exactly what will change before anything is applied.</p>
          <input ref={ref} type="file" accept=".zip" className="hidden" data-testid="backup-import-input" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
          <button data-testid="backup-import-btn" onClick={() => ref.current?.click()} disabled={!!upload || job?.status === "running"}
            className="inline-flex items-center gap-2 border border-slate-200 px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-700 hover:border-primary hover:text-primary disabled:opacity-60">
            {upload ? <><Loader2 className="h-4 w-4 animate-spin" /> {upload.retrying ? `Retrying… (${upload.retrying}/${RETRIES}) ${upload.pct}%` : `Uploading ${upload.pct}%`}</> : <><Upload className="h-4 w-4" /> Choose backup file</>}
          </button>
        </div>
      </div>

      {preview && (
        <div className="mt-4 rounded-xl border border-indigo-200 bg-indigo-50/40 p-4 space-y-3" data-testid="backup-preview">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-semibold text-slate-900">Preview — backup from {(preview.manifest?.exported_at || "").slice(0, 16).replace("T", " ")}{preview.manifest?.exported_by ? ` by ${preview.manifest.exported_by}` : ""}</div>
              <div className="text-xs text-slate-500">{preview.files?.in_file || 0} files in backup · {preview.files?.new || 0} new here</div>
            </div>
            {job?.status !== "running" && <button onClick={() => (job ? (setPreview(null), setJob(null)) : cancel())} data-testid="backup-preview-cancel" className="text-slate-400 hover:text-slate-700" title="Discard"><X className="h-4 w-4" /></button>}
          </div>
          {(preview.warnings || []).map((w) => <div key={w} className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex gap-2"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />{w}</div>)}
          <div className="max-h-72 overflow-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-xs" data-testid="backup-preview-table">
              <thead className="bg-slate-50 text-slate-500 sticky top-0"><tr><th className="text-left px-3 py-2">What</th><th className="text-right px-3 py-2">In backup</th><th className="text-right px-3 py-2">New</th><th className="text-right px-3 py-2">Already here</th><th className="text-left px-3 py-2">On import</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {preview.collections.map((c) => (
                  <tr key={c.name} data-testid={`backup-row-${c.name}`}>
                    <td className="px-3 py-1.5 font-medium text-slate-700">{label(c.name)}{c.kind === "setup" && <span className="ml-1.5 text-[10px] font-semibold text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded-full">setup</span>}
                      {c.replaces_local_users?.length > 0 && <div className="text-[10px] text-amber-700">replaces local: {c.replaces_local_users.join(", ")}</div>}</td>
                    <td className="px-3 py-1.5 text-right">{c.in_file}</td>
                    <td className="px-3 py-1.5 text-right text-emerald-700 font-semibold">{c.new || "—"}</td>
                    <td className="px-3 py-1.5 text-right">{c.matching || "—"}</td>
                    <td className="px-3 py-1.5 text-slate-500">{c.kind === "setup" ? "Replace with backup" : overwrite ? "Add new · overwrite matching" : "Add new · keep matching"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!job && <>
            <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" data-testid="backup-overwrite" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} /> Also overwrite records that already exist here with the backup&apos;s version</label>
            <button data-testid="backup-apply-btn" onClick={apply} className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-semibold"><CheckCircle2 className="h-4 w-4" /> Import backup</button>
          </>}
          {job && (
            <div data-testid="backup-job" className={`text-sm rounded-lg px-3 py-2 ${job.status === "done" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : job.status === "failed" ? "bg-rose-50 text-rose-700 border border-rose-200" : "bg-slate-50 text-slate-600 border border-slate-200"}`}>
              {job.status === "running" && <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Importing… {job.progress || ""}</span>}
              {job.status === "done" && <span data-testid="backup-job-done">Import complete — {sum("added")} added, {sum("replaced")} replaced, {sum("kept")} kept{sum("failed") ? `, ${sum("failed")} failed` : ""} · files: {job.file_stats?.added || 0} added</span>}
              {job.status === "failed" && <span>Import failed: {job.error}</span>}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
